import { useNavigation } from '@react-navigation/native';
import { Image } from 'expo-image';
import { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import { Button } from '@oui/app-core/src/components/Button';
import { Icon } from '@oui/app-core/src/components/Icon';
import { Heading, Label, Text } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';
import { useTheme } from '@oui/app-core/src/styles';
import { NumberParam } from '@oui/app-core/src/types/navigation';

import Holding1 from './frames/BreathHolding_1.png';
import Holding2 from './frames/BreathHolding_2.png';
import Holding3 from './frames/BreathHolding_3.png';
import Holding4 from './frames/BreathHolding_4.png';
import Hyperventilation1 from './frames/Hyperventilation_Default.png';
import Hyperventilation2 from './frames/Hyperventilation_Variant2.png';
import Hyperventilation3 from './frames/Hyperventilation_Variant3.png';
import Hyperventilation4 from './frames/Hyperventilation_Variant4.png';
import { StressBreathingExerciseType } from '../../types/navigation';

const HOLDING_FRAMES = [
  { frame: Holding1, delay: 800, duration: 300 },
  { frame: Holding2, delay: 100, duration: 100 },
  { frame: Holding3, delay: 100, duration: 100 },
  { frame: Holding4, delay: 10000, duration: 300 },
];
const HOLDING_FRAME_SIZE = { width: 160, height: 200 };
const HYPERVENTILATION_FRAMES = [
  { frame: Hyperventilation1, delay: 200, duration: 100 },
  { frame: Hyperventilation2, delay: 200, duration: 100 },
  { frame: Hyperventilation3, delay: 200, duration: 100 },
  { frame: Hyperventilation4, delay: 200, duration: 100 },
];
const HYPERVENTILATION_FRAME_SIZE = { width: 96, height: 200 };

/**
 * Displays introductory info describing the exercise with a button to begin a
 * new exercise. The detailed explanation can be shown/hidden.
 */
export const StressBreathingExplanation = ({
  type,
  detailedExplanation = true,
}: {
  type: StressBreathingExerciseType;
  detailedExplanation: boolean;
}) => {
  const { navigate } = useNavigation();
  const { theme } = useTheme();
  const isHolding = type === 'holding';
  let heading = 'Hyperventilation exercise';
  let frameSize = HYPERVENTILATION_FRAME_SIZE;
  let frames = HYPERVENTILATION_FRAMES;
  let description = `
Breathe fast and deep for 60 seconds to feel lightheaded, dizzy, or have a faster heartbeat, like when you're anxious.

You will do 5 rounds of this exercise.`.trim();

  if (isHolding) {
    heading = 'Breath holding exercise';
    frameSize = HOLDING_FRAME_SIZE;
    frames = HOLDING_FRAMES;
    description = `
Hold your breath for 60 seconds to feel breathless, like during a panic.

You will do 5 rounds of this exercise.`.trim();
  }

  const [frameIndex, setFrameIndex] = useState(0);

  useEffect(() => {
    let loops = 0;
    let nextIndex = 0;
    let timeout: NodeJS.Timeout;
    function advance() {
      nextIndex = (nextIndex + 1) % 4;
      if (nextIndex === 0) {
        loops += 1;
        if (loops > 2 && global.e2e) {
          clearTimeout(timeout);
          // prevent infinite looping animation which causes detox to hang on synchronization
          return;
        }
      }
      timeout = setTimeout(() => {
        setFrameIndex(nextIndex);
        advance();
      }, frames[nextIndex].delay + frames[nextIndex].duration);
    }
    advance();
    return () => clearTimeout(timeout);
    // eslint-disable-next-line
  }, []);

  return (
    <View testID="StressBreathingExplanation" style={{ gap: 25 }}>
      <Heading text={heading} level={1} textAlign="center" style={{ marginBottom: 25 }} />
      <Label text="This exercise will help you lower your anxiety." textAlign="center" />
      <Animated.View
        key={frameIndex.toString()}
        style={[frameSize, { alignSelf: 'center' }]}
        entering={FadeIn.duration(frames[frameIndex].duration)}
        exiting={FadeIn.delay(frames[frameIndex].duration).duration(0)}
      >
        <Image source={frames[frameIndex].frame} style={{ ...StyleSheet.absoluteFillObject }} />
      </Animated.View>
      {detailedExplanation ? <Text text={description} textAlign="center" /> : null}
      <View style={{ gap: 15 }}>
        <Button
          text="Start round 1 of 5"
          iconRight="arrow-right"
          onPress={() => {
            navigate('StressBreathingNew', {
              type,
              practiceID: undefined,
              break: 'false',
              round: '0' as NumberParam,
            });
          }}
          variant="solid"
        />
        <View row style={{ gap: 10, alignSelf: 'center' }}>
          <Icon name="clock" color={theme.color.gray100} size={16} />
          <Text text="1 minute per round" color={theme.color.gray100} size={15} />
        </View>
      </View>
    </View>
  );
};
